import { takeLatest, put, call, select, all, delay } from 'redux-saga/effects';
import find from 'lodash/find';
import get from 'lodash/get';
import { removeItem } from 'yoda-interfaces/lib/Cart/CartApi';
import showGlobalLoader from 'yoda-checkout-components/lib/actions/ShowGlobalLoaderAction';
import { digitalAnalyticsData } from 'yoda-checkout-components/lib/actions/CheckoutAnalyticsAction';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import ItemCountActions from 'yoda-site-components/lib/actions/ItemCountAction';
import ClientLoggerAction from 'yoda-site-components/lib/actions/ClientLoggerAction';
import {
    ANALYTICS_LOCAL_STORAGE_KEY_CART_TOTAL,
    ANALYTICS_ACTIONS_REMOVE_ITEM,
} from 'yoda-checkout-components/lib/common/Constants';
import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import actions from 'yoda-site-components/lib/actions/ProductCartridgeAction';
import config from '../components/SavedItemsProductCartridge/SavedItemsProductCartridge.config';
import {
    REMOVE_CART_ITEM,
    CART_DETAILS_GET_REQUEST,
    REMOVE_CART_ITEM_ERROR,
    UPDATE_ANALYTICS_CLICK_EVENT,
} from '../actionTypes/CartActionTypes';
import {
    updateCartItemInit,
    deleteStoredCartItem,
    storeRemovedCartItem,
} from '../actions/CartItemsAction';

// DA => function which will give me orderItems
export const orderItems = (state) => state.orderItems;
export const cartDetails = (state) => state.cartDetails;

function* removeCartItemSaga(action) {
    try {
        yield put(showGlobalLoader(true));

        const id = action.payload.id || action.payload;
        // DA => get item prveious quantity while updating item quantity
        const findItemData = yield select(orderItems);
        const getModifiedItem = find(findItemData, { id });
        const removeItemAnalyticsData = {
            cartUpdate: {
                productPrevQuantity: getModifiedItem
                    ? getModifiedItem.quantity
                    : action.payload.params.quantity,
                productId: getModifiedItem ? getModifiedItem.id : id,
            },
        };

        yield all([
            // DA => adding productPrevQuantity to redux store. We will need this for Analytics
            put(digitalAnalyticsData(removeItemAnalyticsData)),
            put(updateCartItemInit(id)),
        ]);

        // this delay enables the removal animation to get over
        if (!action.isSaveForLater) {
            yield put(deleteStoredCartItem());
            yield delay(800);
            yield put(storeRemovedCartItem(action.payload));
        }
        const removeAction = {
            ...action,
            payload: {
                id,
                errorHandler: {
                    context: 'CART',
                    errorComponent: 'CART_SECTION',
                },
            },
        };
        const result = yield call(FactorySaga, removeItem, removeAction);

        if (result.isSuccess) {
            // Make the get cart call to refresh the items
            // DA => no cart_update event in case of save_for_later
            if (action.isSaveForLater) {
                const params = {
                    pageType: config.productCartridgeProps.pageType,
                    automationId: config.AUTOMATION_ID,
                    slotId: config.productCartridgeProps.slotId,
                    loader: config.productCartridgeProps.loader,
                    attributes: config.productCartridgeProps.attributes,
                    customRenderProductCard: config.productCartridgeProps.customRenderProductCard,
                };
                yield all([
                    put(ItemCountActions.updateItemCountAction()),
                    put(actions.getProductListForCartridgeSlot(params)),
                    put({ type: CART_DETAILS_GET_REQUEST, requestType: 'REMOVE_ITEM' }),
                ]);
                LocalStorage.setData(
                    'CART_UPDATED_MANUALLY',
                    'true',
                    'CART_UPDATED_MANUALLY',
                    undefined,
                    undefined,
                    0.2
                );
            } else {
                yield all([
                    put(ItemCountActions.updateItemCountAction()),
                    put({
                        type: UPDATE_ANALYTICS_CLICK_EVENT,
                        payload: ANALYTICS_ACTIONS_REMOVE_ITEM,
                    }),
                    put({
                        type: CART_DETAILS_GET_REQUEST,
                        retainRemovalMessage: true,
                        requestType: 'REMOVE_ITEM',
                    }),
                ]);
                LocalStorage.setData(
                    'CART_UPDATED_MANUALLY',
                    'true',
                    'CART_UPDATED_MANUALLY',
                    undefined,
                    undefined,
                    0.2
                );
            }
            LocalStorage.setData('TOTAL_EXCEEDED_ERROR', 0, true);
            LocalStorage.removeData('SDPUStoreInfo', true);
        } else {
            /* Construct payload required to log as part of <= MCYODA-25036
             * */
            const cartData = yield select(cartDetails);
            const responseCode = get(result, 'response.status', '400');
            const skuId = get(getModifiedItem, 'sku.id', id);
            const lineItemQuantity = get(getModifiedItem, 'quantity', 0);
            const bagCount = get(cartData, 'data.bag.itemCount', 0);
            const cartValue = LocalStorage.getData(ANALYTICS_LOCAL_STORAGE_KEY_CART_TOTAL);
            yield put(
                ClientLoggerAction.logError(
                    'unable to remove item on cart',
                    `responseCode: ${responseCode} | skuId:${skuId} | bagCount:${bagCount} | lineItemQuantity:${lineItemQuantity} | cartValue:${cartValue}`
                )
            );
            yield all([
                put(deleteStoredCartItem()),
                // Need to show appropriate error in this case
                put({ type: REMOVE_CART_ITEM_ERROR, result }),
                put(showGlobalLoader(false)),
            ]);
        }
    } catch (ex) {
        yield put(showGlobalLoader(false));
    }
}

const watchRemoveCartItem = function* watchRemoveCartItem() {
    yield takeLatest(REMOVE_CART_ITEM, removeCartItemSaga);
};
watchRemoveCartItem.sagaName = 'watchRemoveCartItem';

export default watchRemoveCartItem;
